import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Table, Form, Pagination, Dropdown, InputGroup, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileDownload,
  faSort,
  faSearch,
  faPrint,
  faPlusCircle,
  faClock,
  faTimesCircle,
  faEye,
  faCheckCircle,
  faShieldAlt,
  faFolder,
  faNoteSticky,
  faHourglassHalf
} from "@fortawesome/free-solid-svg-icons";
import { getBondsDataService } from "../../services/BondsServices";
import { exportTableDataAsCSV, exportTableAsPDF, exportAllDataAsPDF2 } from "../../utils/systemUtils";
import { Settings } from "react-feather";
import { BOND_STATUS_LABEL, ROLES } from "../../types";
import { NavLink, useNavigate } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import moment from "moment/moment";

const OAGUserLandingPage = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const user = useSelector((state) => state?.user);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const bondsDaTableRef = useRef(null);
  const fullHiddenBondsDaTableRef = useRef(null);
  const [bondsData, setBondsData] = useState([]);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const [pageFiltered, setPageFiltered] = useState("");
  const [sortDirection, setSortDirection] = useState("ascending");
  const [currentData, setCurrentData] = useState([]);
  const [filterBy, setFilterBy] = useState("my_transcripts");
  const navigate = useNavigate();
  const searchEnableRef = useRef(false)
  let USDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  useEffect(() => {
    getBondsData(user.userinfo.email);
  }, [user.userinfo.email]);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      setIsLoggedIn(true)
    }
  }, [authState]);

  useEffect(() => {
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    setCurrentData(currentItems);
  }, [filteredData, currentPage]);

  useEffect(() => {
    if (!bondsData || bondsData.length === 0) return;

    // Reset when search is cleared
    if (!searchTerm || searchTerm.trim().length === 0) {
      setFilteredData(bondsData);
      onChangeFilter(filterBy);
      searchEnableRef.current = false;
      return;
    }

    const term = searchTerm.toLowerCase().trim();

    const filtered = filteredData.filter((bond) => {
      // Safe string helper
      const safe = (val) =>
        val ? val.toString().toLowerCase() : '';

      const lawFirm = safe(bond.law_firm);
      const aagName = safe(bond.aag_name);
      const issuer = safe(bond.Issuer);
      const title = safe(bond.title);
      const bondNumber = safe(bond.bond_issue_number);
      const bondStatus = safe(bond.bond_status);
      const parDollar = safe(bond.par_dollar_amount);
      const opinionRequestedBy = safe(bond.opinion_requested_by);

      const closingDate = safe(bond.closing_date);
      const submissionDate = safe(bond.submission_date); // ✅ ADDED

      // AAG search
      const matchAagUser =
        Array.isArray(bond.aag_userinfo) &&
        bond.aag_userinfo.some((person) =>
          safe(person?.FirstName).includes(term)
        );

      // ✅ ADDED: Bond Counsel search
      const matchBondCounsel =
        Array.isArray(bond.bondcounsel_userinfo) &&
        bond.bondcounsel_userinfo.some((person) =>
          safe(person?.FirstName).includes(term) ||
          safe(person?.LastName).includes(term)
        );

      const textMatch =
        lawFirm.includes(term) ||
        aagName.includes(term) ||
        issuer.includes(term) ||
        title.includes(term) ||
        bondNumber.includes(term) ||
        bondStatus.includes(term) ||
        parDollar.includes(term) ||
        opinionRequestedBy.includes(term);

      const dateMatch =
        closingDate.includes(term) ||
        submissionDate.includes(term); // ✅ ADDED

      return textMatch || dateMatch || matchAagUser || matchBondCounsel; // ✅ updated
    });

    setFilteredData(filtered);

  }, [bondsData, searchTerm, filterBy]);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleSorting = (key) => {
    const sortedItems = [...filteredData]?.sort((a, b) => {
      let itemA = a[key];
      let itemB = b[key];

      if (key === 'par_dollar_amount') {
        itemA = parseFloat(itemA);
        itemB = parseFloat(itemB);
      } else if (key === 'bondcounsel_userinfo') {
        itemA = itemA?.map(user => `${user?.FirstName} ${user?.LastName}`).join(' ').toUpperCase();
        itemB = itemB?.map(user => `${user?.FirstName} ${user?.LastName}`).join(' ').toUpperCase();
      }
      else if (key === 'aag_userinfo') {
        // itemA = `${itemA?.FirstName} ${itemA?.LastName}`.toUpperCase();
        // itemB = `${itemB?.FirstName} ${itemB?.LastName}`.toUpperCase();
        itemA = itemA?.map(user => `${user?.FirstName} ${user?.LastName}`).join(' ').toUpperCase();
        itemB = itemB?.map(user => `${user?.FirstName} ${user?.LastName}`).join(' ').toUpperCase();
      }
      else if (key === 'closing_date') {
        const parseDate = (dateStr) => {
          if (!dateStr) return 0;
          const [month, day, year] = dateStr.split('/');
          return new Date(year, month - 1, day).getTime();
        };

        itemA = parseDate(itemA);
        itemB = parseDate(itemB);
      }
      else {
        itemA = itemA?.toString().toUpperCase();
        itemB = itemB?.toString().toUpperCase();
      }

      if (itemA < itemB) {
        return sortDirection === "ascending" ? -1 : 1;
      }
      if (itemA > itemB) {
        return sortDirection === "ascending" ? 1 : -1;
      }
      return 0;
    });

    setFilteredData(sortedItems);
    setSortDirection(sortDirection === "ascending" ? "descending" : "ascending");
  };


  async function getBondsData(user_email) {
    try {
      let bonds_respo = await getBondsDataService(user_email);

      bonds_respo = bonds_respo.map((bond) => {
        let formatted_date = ''
        let submission_date_formatted_date = ''
        if (bond.closing_date) {
          let parsedDate = moment(bond.closing_date, ['DD-MM-YYYY', moment.ISO_8601, 'MMMM D YYYY'], true);
          if (!parsedDate.isValid()) {
            parsedDate = moment(new Date(bond.closing_date))
          }
          if (parsedDate.isValid()) {
            formatted_date = parsedDate.format("MM/DD/YYYY")
          }

        }
         if (bond.submission_date) {
          let submission_date_parsedDate = moment(bond.submission_date, ['DD-MM-YYYY', moment.ISO_8601, 'MMMM D YYYY'], true);
          if (!submission_date_parsedDate.isValid()) {
            submission_date_parsedDate = moment(new Date(bond.submission_date))
          }
          if (submission_date_parsedDate.isValid()) {
            submission_date_formatted_date = submission_date_parsedDate.format("MM/DD/YYYY")
          }

        }

        return {
          ...bond,
          closing_date: formatted_date,
          submission_date:submission_date_formatted_date
        }

      })
      setBondsData(bonds_respo);
      setError("");
    } catch (error) {
      setError("Error fetching Bonds");
    }
  }

  const onChangeFilter = async (target) => {
    setFilterBy(target)
    const user_email = user.userinfo.email;
    setCurrentPage(1);
    let temp = [...bondsData];

    if (target === "my_transcripts") {
      temp = Array.isArray(bondsData) && bondsData.length > 0 && bondsData.filter((bond) => {
        // Get the array of AAG emails, lowercased
        const aagEmails = bond.aag_userinfo?.map(
          (user) => user.EmailID?.toLowerCase()
        ) || [];

        // Get the array of legal assistant emails, lowercased
        const assistantEmails = (bond.legal_assistant_userinfo || [])
          .map(assistant => assistant.EmailID?.toLowerCase())
          .filter(email => email);
       // console.log("assistantEmails", assistantEmails)
       // console.log("aagEmails", aagEmails)
       // console.log("user_email", user_email)
        // Combine them and check if they include the user email (case-insensitive)
        return [...aagEmails, ...assistantEmails].includes(user_email?.toLowerCase());
      });
      temp = temp.filter((bond) => bond.bond_status === BOND_STATUS_LABEL.New ||
        bond.bond_status === BOND_STATUS_LABEL.Pending)
    }


    if (target === "unassigned") {

      temp = bondsData.filter((bond) =>
        bond.bondcounsel_userinfo.length === 0 || bond.aag_userinfo.length === 0
      );
    }

    switch (target) {
      case "all":

        break;
      // case "my_transcripts":
      //   temp = bondsData.filter((bond) => bond.aag_userinfo.EmailID?.toString().toUpperCase() === user_email?.toString().toUpperCase()
      //     || bond.legal_assistant_userinfo.EmailID?.toString().toUpperCase() === user_email?.toString().toUpperCase());
      //   break;
      case "in_progress":
        temp = bondsData.filter((bond) =>
          bond.bond_status !== BOND_STATUS_LABEL.RegistrationComplete &&
          bond.bond_status !== BOND_STATUS_LABEL.Cancelled
        );
        break;

      case "completed":
        try {

          let bonds_respo = await getBondsDataService(
            user_email,
            "R"
          );

          bonds_respo = bonds_respo.map((bond) => {

            let formatted_date = "";
            let submission_date = "";

            if (bond.closing_date) {

              let parsedDate = moment(bond.closing_date);

              if (parsedDate.isValid()) {
                formatted_date = parsedDate.format("MM/DD/YYYY");
              }

            }

            if (bond.submission_date) {

              let parsedDate = moment(bond.submission_date);

              if (parsedDate.isValid()) {
                submission_date = parsedDate.format("MM/DD/YYYY");
              }

            }

            return {
              ...bond,
              closing_date: formatted_date,
              submission_date: submission_date
            };

          });

          // setBondsData(bonds_respo);
          setFilteredData(bonds_respo);
          setCurrentPage(1);
          setError("");

          return; // stop execution so bottom setFilteredData doesn't override

        } catch (error) {

          console.error(error);
          setError("Error fetching Bonds");

        }

        break;

      case "unassigned":
        temp = bondsData.filter(bondData =>
          bondData.aag_userinfo.length === 0 || bondData.aag_userinfo.some(user => !user.EmailID)
        );

        break;
      default:
        // Handle the default case if needed
        break;
    }

    setFilteredData(temp);
  };

  if (!isLoggedIn) {
    return navigate("/");
  }


  const statusIcons = {
    "New": <FontAwesomeIcon className="status-icon green" icon={faPlusCircle}></FontAwesomeIcon>,
    "Registered": <FontAwesomeIcon className="status-icon emerald-green" icon={faCheckCircle}></FontAwesomeIcon>,
    "Delayed": <FontAwesomeIcon className="status-icon amber" icon={faClock}></FontAwesomeIcon>,
    "Cancelled": <FontAwesomeIcon className="status-icon red" icon={faTimesCircle}></FontAwesomeIcon>,
    "Opinion Issued": <FontAwesomeIcon className="status-icon indigo" icon={faShieldAlt}></FontAwesomeIcon>,
    "Under Review": <FontAwesomeIcon className="status-icon blue" icon={faEye}></FontAwesomeIcon>

  }

  return (
    <div className="dashboard-wrapper">
      <div className="col-md-2 side-box-container">
        <div
          className="side-box"
        >
          <button
            className={`side-nav-btn btn btn-link mb-2 ${filterBy === 'all' ? "active" : ''}`}
            onClick={() => onChangeFilter("all")}
          >
            <FontAwesomeIcon className="status-icon" icon={faFolder}></FontAwesomeIcon> All
          </button>
          <button className={`side-nav-btn btn btn-link mb-2 ${filterBy === 'unassigned' ? "active" : ''}`} onClick={() => onChangeFilter("unassigned")}>
            <FontAwesomeIcon className="status-icon" icon={faNoteSticky} ></FontAwesomeIcon> Unassigned
          </button>
          <button
            className={`side-nav-btn btn btn-link mb-2 ${filterBy === 'my_transcripts' ? "active" : ''}`}
            onClick={() => onChangeFilter("my_transcripts")}
          >
            <FontAwesomeIcon className="status-icon" icon={faNoteSticky} ></FontAwesomeIcon> My Transcripts
          </button>
          <button
            className={`side-nav-btn btn btn-link mb-2 ${filterBy === 'in_progress' ? "active" : ''}`}
            onClick={() => onChangeFilter("in_progress")}
          >
            <FontAwesomeIcon className="status-icon" icon={faHourglassHalf} ></FontAwesomeIcon> In Progress
          </button>
          <button
            className={`side-nav-btn btn btn-link mb-2 ${filterBy === 'completed' ? "active" : ''}`}
            onClick={() => onChangeFilter("completed")}
          >
            <FontAwesomeIcon className="status-icon" icon={faCheckCircle} ></FontAwesomeIcon> Registered
          </button>
        </div>
      </div>
      <div className="col-md-10 main-box-container">
        <div>
          <div className="col-12">
            <div className="dashboard-header">
              <h1>OAG Landing Page</h1>
            </div>
            {error && (
              <div className="alert alert-warning">
                <strong>Warning!</strong> {error}
              </div>
            )}
          </div>
        </div>
        <div className="table-container">
          <div className="child-div">
            <div style={{ width: "98%" }}>
              <div className="row search-print-container">
                <div className="col-md-3">
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={handleSearch}
                    />
                  </InputGroup>
                </div>
                <div className="col-md-6"></div>
                <div className="col-md-3 export-container">
                  <Button
                    className="btn-download"
                    variant="primary"
                    onClick={(event) => {
                      event.stopPropagation();
                      exportTableDataAsCSV(
                        fullHiddenBondsDaTableRef,
                        ROLES.OFFICE_OF_THE_ATTORNEY_GENERAL_USER
                      );
                    }}
                  >
                    <FontAwesomeIcon icon={faFileDownload} /> Download
                  </Button>
                  <Button
                    variant="primary"
                    onClick={(event) => {
                      event.stopPropagation();
                      exportTableAsPDF(fullHiddenBondsDaTableRef, "BCU");
                    }}
                  >
                    <FontAwesomeIcon icon={faPrint} /> Print
                  </Button>
                </div>
              </div>
              <Table
                ref={bondsDaTableRef}
              >
                <thead>
                  <tr style={{ whiteSpace: "nowrap", fontSize: "0.9em" }}>
                    <th
                      onClick={() => handleSorting("bond_issue_number")}
                      style={{ resize: "horizontal", overflow: "auto" }}
                    >
                      Issue/Opinion #
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSorting("closing_date")}
                      style={{ resize: "horizontal", overflow: "auto" }}
                    >
                      Closing Date
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSorting("Issuer")}
                      style={{ resize: "horizontal", overflow: "auto" }}
                    >
                      Issuer
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSorting("title")}
                      style={{ resize: "horizontal", overflow: "auto" }}
                    >
                      Title
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSorting("par_dollar_amount")}
                      style={{ resize: "horizontal", overflow: "auto" }}
                    >
                      Par Dollar Amount
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSorting("bond_status")}
                      style={{ resize: "horizontal", overflow: "auto" }}
                    >
                      Status
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSorting("aag_userinfo")}
                      style={{ resize: "horizontal", overflow: "auto" }}
                    >
                      AAG Name
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSorting("law_firm")}
                      style={{ resize: "horizontal", overflow: "auto" }}
                    >
                      Law Firm{" "}
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSorting("bondcounsel_userinfo")}
                      style={{ resize: "horizontal", overflow: "auto" }}
                    >
                      Bond Counsel Name
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th
                      onClick={() => handleSorting("submission_date")}
                      style={{ resize: "horizontal", overflow: "auto" }}
                    >
                      Submission Date
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.map((bond, index) => (
                    <tr key={bond.id}>
                      <td>
                        {bond.bond_issue_number && (
                          <a
                            href={`/bond-details/${bond.bond_issue_number}/edit`}
                            target="_self"
                            rel="noopener noreferrer"
                          >
                            {bond.bond_issue_number}
                          </a>
                        )}
                      </td>
                      <td>{bond.closing_date}</td>
                      <td>{bond.Issuer}</td>
                      <td>{bond.title}</td>
                      <td>{USDollar.format(bond.par_dollar_amount)}</td>
                      <td className="no-resize">{statusIcons[bond.bond_status]} {bond.bond_status}</td>
                      <td style={{ maxWidth: '200px' }}>
                        {bond.aag_userinfo
                          .map((aag) => `${aag.FirstName} ${aag.LastName}`)
                          .join(", ")}
                      </td>
                      <td>{bond.law_firm}</td>
                      <td style={{ maxWidth: '200px' }}>
                        {bond.bondcounsel_userinfo
                          .map(
                            (counsel) =>
                              `${counsel.FirstName} ${counsel.LastName}`
                          )
                          .join(", ")}
                      </td>
                      <td>{bond.submission_date}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* this table is hidden  */}
              <div style={{ display: "none" }}>
                <Table
                  responsive
                  bordered
                  striped
                  hover
                  size="sm"
                  ref={fullHiddenBondsDaTableRef}
                >
                  <thead>
                    <tr style={{ whiteSpace: "nowrap", fontSize: "0.9em" }}>
                      <th>
                        Issue/Opinion #
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Closing Date
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Issuer
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Title
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Par Dollar Amount
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Status
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        AAG Name
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Law Firm{" "}
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Bond Counsel Name
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Submission Date
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData?.map((bond, index) => (
                      <tr key={bond.id}>
                        <td>
                          {bond.bond_issue_number && (
                            <a
                              href={`/bond-details/${bond.bond_issue_number}/edit`}
                              target="_self"
                              rel="noopener noreferrer"
                            >
                              {bond.bond_issue_number}
                            </a>
                          )}
                        </td>
                        <td>{bond.closing_date}</td>
                        <td>{bond.Issuer}</td>
                        <td>{bond.title}</td>
                        <td>{USDollar.format(bond.par_dollar_amount)}</td>
                        <td>{bond.bond_status}</td>
                        <td>
                          {bond.aag_userinfo
                            .map((aag) => `${aag.FirstName} ${aag.LastName}`)
                            .join(", ")}
                        </td>
                        <td>{bond.law_firm}</td>
                        <td>
                          {bond.bondcounsel_userinfo
                            .map(
                              (counsel) =>
                                `${counsel.FirstName} ${counsel.LastName}`
                            )
                            .join(", ")}
                        </td>
                        <td>{bond.submission_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {/* ------------------------------- */}
              <div className="d-flex justify-content-between">
                <NavLink
                  to={"/upload-transcript"}
                  className={"btn btn-primary me-2 mb-2"}
                >
                  Upload New Transcript
                </NavLink>
                <Pagination className="float-end">
                  <Pagination.First
                    onClick={() => paginate(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Item>
                    Page {currentPage} of {Math.ceil(filteredData.length / itemsPerPage)}
                  </Pagination.Item>
                  <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
                  />
                  <Pagination.Last
                    onClick={() => paginate(Math.ceil(filteredData.length / itemsPerPage))}
                    disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
                  />
                </Pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OAGUserLandingPage;

